// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { AccountType } from '@shared/utils/cache/Accounts/Account.types';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { QuoteValid } from '@shared/utils/Quotes/QuoteValid';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { DataCache } from '../DataCache';
import { BaseSettings } from '../Settings/BaseGeneralSettingsWrapper';
export class PositionBalanceID {
    constructor(tradableID, productType) {
        this.tradableID = tradableID;
        this.productType = productType;
    }
    get ProductType() {
        return this.productType;
    }
    equals(other) {
        return this.productType === other.productType && this.tradableID === other.tradableID;
    }
    toString() {
        return `${this.tradableID}-${this.productType}`;
    }
}
export class PositionsBalance {
    constructor() {
        this.Positions = {};
        this.StrikePrice = null;
        this.LongQty = null; // always in lots
        this.ShortQty = null; // always in lots
        this.GrossPnL = null;
        this.NetPositionQty = null; // always in lots
        this.PositionValue = null;
        this.GrossExposure = null;
        this.BreakEvenPoint = null;
        this.AverageLong = null;
        this.AverageShort = null;
        this.NetExposure = null;
        this.CurrentPrice = null;
        this.NetPnL = null;
        this.ShortFullCost = null;
        this.LongFullCost = null;
        this.isMultiAssets = false;
        this.maximumAmountPrecisionAccount = null;
        this._positionBalanceID = null;
    }
    get positionBalanceID() {
        if (!isNullOrUndefined(this._positionBalanceID)) {
            return this._positionBalanceID;
        }
        return new PositionBalanceID(-1, ProductType.General);
    }
    get Instrument() {
        var _a;
        return (_a = Object.values(this.Positions)[0]) === null || _a === void 0 ? void 0 : _a.Instrument;
    }
    get Position() {
        return Object.values(this.Positions)[0];
    }
    get Account() {
        var _a;
        return (_a = Object.values(this.Positions)[0]) === null || _a === void 0 ? void 0 : _a.Account;
    }
    getAccountWithMaxAmountPrecision() {
        return this.maximumAmountPrecisionAccount;
    }
    get ProductType() {
        return this.positionBalanceID.ProductType;
    }
    get hasSameRoute() {
        const positionValues = Object.values(this.Positions);
        if (positionValues.length > 0) {
            const currentRouteID = positionValues[0].Instrument.Route;
            for (const position of positionValues) {
                if (currentRouteID !== position.Instrument.Route) {
                    return false;
                }
            }
        }
        return true;
    }
    removePosition(position) {
        delete this.Positions[position.PositionId];
        if (Object.keys(this.Positions).length !== 0) {
            this.RecalculatePositionBalance();
            this.updateAccountWithMaximumAmountPrecision();
        }
    }
    addPosition(position) {
        if (isNullOrUndefined(this._positionBalanceID)) {
            this._positionBalanceID = new PositionBalanceID(position.Instrument.InstrumentTradableID, position.ProductType);
        }
        this.Positions[position.PositionId] = position;
        this.CreatedDate = Math.min(position.UTCDateTime, this.CreatedDate);
        this.RecalculatePositionBalance();
        this.updateAccountWithMaximumAmountPrecision();
    }
    RecalculatePositionBalance() {
        const totals = this.createEmptyTotals();
        const calculateInServerCurrency = this.updateBalanceAsset();
        for (const position of Object.values(this.Positions)) {
            const recordValues = this.getPositionRecordValues(position, calculateInServerCurrency);
            this.addPositionRecordToTotals(totals, position, recordValues);
        }
        this.applyPositionBalanceTotals(totals);
    }
    createEmptyTotals() {
        return {
            grossPnL: 0,
            netPnL: 0,
            shortExposure: 0,
            longExposure: 0,
            shortFullCost: 0,
            longFullCost: 0,
            positionValue: 0,
            longQty: 0,
            shortQty: 0
        };
    }
    updateBalanceAsset() {
        const assetName = this.getBalanceAssetName();
        const calculateInServerCurrency = this.isMultiAssets;
        this.Asset = DataCache.GetAssetByName(calculateInServerCurrency ? DataCache.baseCurrency : assetName);
        return calculateInServerCurrency;
    }
    getBalanceAssetName() {
        let assetName;
        this.isMultiAssets = false;
        for (const position of Object.values(this.Positions)) {
            const positionAssetName = this.getPositionAssetName(position);
            assetName !== null && assetName !== void 0 ? assetName : (assetName = positionAssetName);
            if (assetName !== positionAssetName) {
                this.isMultiAssets = true;
                break;
            }
        }
        return assetName;
    }
    getPositionAssetName(position) {
        const account = position.Account;
        return account.AccountType === AccountType.MultiAsset
            ? position.Instrument.CalculationCurrency
            : account.BaseCurrency;
    }
    getPositionRecordValues(position, calculateInServerCurrency) {
        const useAccCurrency = !calculateInServerCurrency;
        return {
            grossPnL: position.getGrossPnL(useAccCurrency),
            netPnL: position.getNetPnL(useAccCurrency),
            exposureValue: position.getExpositionValue(useAccCurrency),
            positionValue: position.getPositionValue(useAccCurrency)
        };
    }
    addPositionRecordToTotals(totals, position, recordValues) {
        if (position.getGrossPnL(true) !== 0) {
            totals.grossPnL += recordValues.grossPnL;
        }
        if (position.getNetPnL(true) !== 0) {
            totals.netPnL += recordValues.netPnL;
        }
        if (position.BuySell === OperationType.Buy) {
            this.addLongPositionTotals(totals, position, recordValues);
        }
        else {
            this.addShortPositionTotals(totals, position, recordValues);
        }
        totals.positionValue += recordValues.positionValue;
    }
    addLongPositionTotals(totals, position, recordValues) {
        totals.longQty += position.Amount;
        totals.longExposure += recordValues.exposureValue;
        totals.longFullCost += position.Amount * position.Price;
    }
    addShortPositionTotals(totals, position, recordValues) {
        totals.shortQty += position.Amount;
        totals.shortExposure += recordValues.exposureValue;
        totals.shortFullCost += position.Amount * position.Price;
    }
    applyPositionBalanceTotals(totals) {
        const longAndShortExposure = totals.longExposure - totals.shortExposure;
        const longAndShortFullCost = totals.longFullCost - totals.shortFullCost;
        this.Symbol = this.Position.GetInstrumentDisplayName();
        this.ExpDate = this.getExpirationDate();
        this.StrikePrice = this.getStrikePrice();
        this.LongQty = totals.longQty;
        this.ShortQty = totals.shortQty;
        this.GrossPnL = totals.grossPnL;
        this.NetPnL = totals.netPnL;
        this.ShortFullCost = totals.shortFullCost;
        this.LongFullCost = totals.longFullCost;
        this.NetPositionQty = totals.longQty - totals.shortQty;
        this.PositionValue = totals.positionValue;
        this.GrossExposure = totals.longExposure + totals.shortExposure;
        this.BreakEvenPoint = this.getBreakEvenPoint(longAndShortFullCost);
        this.AverageLong = this.getAveragePrice(totals.longFullCost, this.LongQty);
        this.AverageShort = this.getAveragePrice(totals.shortFullCost, this.ShortQty);
        this.NetExposure = longAndShortExposure;
        this.CurrentPrice = this.GetCurrentPrice(this.Instrument, totals.longQty, totals.shortQty);
    }
    getExpirationDate() {
        return isNullOrUndefined(this.Instrument) || isNullOrUndefined(this.Instrument.ExpDate)
            ? null
            : DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(new Date(this.Instrument.ExpDateReal.getTime()));
    }
    getStrikePrice() {
        const instrument = this.Instrument;
        return isNullOrUndefined(instrument) || instrument.StrikePrice <= 0 ? 0 : instrument.StrikePrice;
    }
    getBreakEvenPoint(longAndShortFullCost) {
        if (isNullOrUndefined(this.NetPositionQty) || this.NetPositionQty === 0) {
            return NaN;
        }
        if (longAndShortFullCost === 0) {
            return 0;
        }
        const breakeven = longAndShortFullCost / this.NetPositionQty;
        return breakeven >= 0 ? breakeven : NaN;
    }
    getAveragePrice(fullCost, qty) {
        return isNullOrUndefined(qty) ? 0 : fullCost / qty;
    }
    updateAccountWithMaximumAmountPrecision() {
        let maxPrecision = -1;
        for (const posID in this.Positions) {
            const pos = this.Positions[posID];
            const currentPrecision = pos.Instrument.getAmountPrecision(BaseSettings.isQuantityInLots, pos.Account, this.ProductType);
            if (currentPrecision > maxPrecision) {
                maxPrecision = currentPrecision;
                this.maximumAmountPrecisionAccount = pos.Account;
            }
        }
    }
    GetCurrentPrice(instrument, buyAmount, sellAmount) {
        if (isNullOrUndefined(instrument) || isNullOrUndefined(instrument.DataCache)) {
            return -1;
        }
        if (instrument.CalcPriceOnTrade()) {
            return this.getCurrentTradePrice(instrument);
        }
        const lastQuote = instrument.GetLastQuote(QuoteValid.Last);
        if (isNullOrUndefined(lastQuote)) {
            return -1;
        }
        const spreadPlan = instrument.DataCache.GetSpreadPlan(null);
        const currentPrice = this.getCurrentQuoteSidePrice(instrument, lastQuote, spreadPlan, buyAmount, sellAmount);
        return this.replaceZeroPriceWithPrevClose(instrument, currentPrice);
    }
    getCurrentTradePrice(instrument) {
        const lastPrice = instrument.Level1.GetLastPrice();
        return isNaN(lastPrice) || lastPrice >= 0 ? lastPrice : -1;
    }
    getCurrentQuoteSidePrice(instrument, lastQuote, spreadPlan, buyAmount, sellAmount) {
        return buyAmount - sellAmount > 0
            ? this.getBidPrice(instrument, lastQuote, spreadPlan)
            : this.getAskPrice(instrument, lastQuote, spreadPlan);
    }
    getAskPrice(instrument, lastQuote, spreadPlan) {
        return !isNaN(lastQuote.Ask)
            ? lastQuote.AskSpread_SP_Ins(spreadPlan, instrument)
            : this.getMissingQuoteSideFallbackPrice(instrument, lastQuote);
    }
    getBidPrice(instrument, lastQuote, spreadPlan) {
        return !isNaN(lastQuote.Bid)
            ? lastQuote.BidSpread_SP_Ins(spreadPlan, instrument)
            : this.getMissingQuoteSideFallbackPrice(instrument, lastQuote);
    }
    getMissingQuoteSideFallbackPrice(instrument, lastQuote) {
        return lastQuote.IsValid ? 0 : instrument.Level1.GetLastPrice();
    }
    replaceZeroPriceWithPrevClose(instrument, currentPrice) {
        if (currentPrice !== 0) {
            return currentPrice;
        }
        const prevClose = instrument.Level1.GetPrevClose();
        return prevClose >= 0 ? prevClose : currentPrice;
    }
}
